import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { useNavigate } from 'react-router-dom';
import { MagacinArrival, MagacinProduct } from '../types';
import { magacinArrivalsAPI, magacinProductsAPI } from '../services/api';
import './Magacin.css';

const fmtDate = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

const MagacinArrivals: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [products, setProducts] = useState<MagacinProduct[]>([]);
  const [arrivals, setArrivals] = useState<MagacinArrival[]>([]);

  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyProduct, setHistoryProduct] = useState<MagacinProduct | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [p, a] = await Promise.all([magacinProductsAPI.getAll(), magacinArrivalsAPI.getAll()]);
      setProducts(p);
      setArrivals(a);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju istorije dolazaka');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const arrivalsByProduct = useMemo(() => {
    const map = new Map<string, MagacinArrival[]>();
    for (const a of arrivals) {
      const pid = typeof a.productId === 'string' ? a.productId : (a.productId as any)?._id;
      if (!pid) continue;
      const curr = map.get(pid) || [];
      curr.push(a);
      map.set(pid, curr);
    }
    // ensure newest first
    map.forEach((v, k) => {
      v.sort(
        (x: MagacinArrival, y: MagacinArrival) => new Date(y.arrivalDate).getTime() - new Date(x.arrivalDate).getTime()
      );
      map.set(k, v);
    });
    return map;
  }, [arrivals]);

  const rows = useMemo(() => {
    return products.map((p) => {
      const list = arrivalsByProduct.get(p._id) || [];
      const suppliers = uniq(list.map((x) => x.supplier));
      return {
        ...p,
        _arrivalCount: list.length,
        _suppliers: suppliers.join(', '),
        _arrivals: list
      } as any;
    });
  }, [products, arrivalsByProduct]);

  const classBody = (row: any) => (
    <Tag value={`Klasa ${row.productClass}`} severity={row.productClass === 'A' ? 'success' : row.productClass === 'B' ? 'warning' : 'danger'} />
  );

  const stockBody = (row: any) => `${row.availableWeight ?? 0} ${row.unit || ''}`.trim();

  const openHistory = (row: any) => {
    setHistoryProduct(row as MagacinProduct);
    setHistoryVisible(true);
  };

  const historyRows = useMemo(() => {
    if (!historyProduct) return [];
    return arrivalsByProduct.get(historyProduct._id) || [];
  }, [historyProduct, arrivalsByProduct]);

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Istorija dolazaka</h2>
          <div className="text-600 mt-1">Istorija dolazaka proizvoda</div>
        </div>
        <div className="flex gap-2">
          <Button label="Nazad na dashboard" icon="pi pi-arrow-left" severity="secondary" onClick={() => navigate('/dashboard')} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable value={rows} loading={loading} paginator rows={10} emptyMessage="Nema dolazaka" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
          <Column field="name" header="Proizvod" sortable />
          <Column field="category" header="Kategorija" sortable />
          <Column header="Klasa" body={classBody} sortable sortField="productClass" style={{ width: '140px' }} />
          <Column header="Trenutno stanje" body={stockBody} sortable sortField="availableWeight" style={{ width: '180px' }} />
          <Column field="_suppliers" header="Dobavljači" />
          <Column field="_arrivalCount" header="Broj dolazaka" sortable style={{ width: '160px' }} />
          <Column
            header="Akcije"
            body={(row: any) => (
              <Button
                label="Prikaži istoriju"
                icon="pi pi-history"
                className="p-button-text"
                onClick={() => openHistory(row)}
              />
            )}
            style={{ width: '170px' }}
          />
        </DataTable>
      </Card>

      <Dialog
        header={`Istorija dolazaka${historyProduct ? ` - ${historyProduct.name}` : ''}`}
        visible={historyVisible}
        onHide={() => setHistoryVisible(false)}
        style={{ width: '820px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        draggable={false}
        modal
      >
        {historyProduct && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{historyProduct.name}</div>
            <div className="text-600">Kategorija: {historyProduct.category}</div>
            <div className="text-600">
              Trenutno stanje: {historyProduct.availableWeight} {historyProduct.unit}
            </div>
          </div>
        )}

        <DataTable value={historyRows} paginator rows={5} emptyMessage="Nema dolazaka" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
          <Column header="Datum dolaska" body={(r: MagacinArrival) => fmtDate(r.arrivalDate)} sortable sortField="arrivalDate" style={{ width: '160px' }} />
          <Column header="Količina" body={(r: MagacinArrival) => `${r.quantity} ${r.unit}`.trim()} sortable sortField="quantity" style={{ width: '160px' }} />
          <Column field="supplier" header="Dobavljač" sortable />
          <Column field="batchNumber" header="Broj serije" sortable style={{ width: '160px' }} />
          <Column field="notes" header="Napomene" />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default MagacinArrivals;


